import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Calculator, Plus, Trash2, Save, Edit2, GripVertical, 
  Settings, DollarSign, ChevronDown, ChevronRight, Eye, EyeOff, Copy
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminEstimatorPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [projectTypes, setProjectTypes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('types');
  const [expandedTypes, setExpandedTypes] = useState([]);

  useEffect(() => {
    // Check new session-based auth first (Google login or new password login)
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (isLoggedIn) {
      setIsAuthenticated(true);
      fetchData();
      return;
    }
    
    // Fall back to old session check
    const sessionId = localStorage.getItem('admin_session_id');
    const adminUser = localStorage.getItem('admin_user');
    if (sessionId || adminUser) {
      setIsAuthenticated(true);
      fetchData();
      return;
    }
    
    // Not authenticated
    navigate('/admin/login');
  }, [navigate]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [typesRes, questionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/estimator/project-types/all`),
        axios.get(`${BACKEND_URL}/api/estimator/questions`)
      ]);
      setProjectTypes(typesRes.data);
      setQuestions(questionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateType = async (typeId, data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/estimator/project-types/${typeId}`, data);
      await fetchData();
      setEditingType(null);
    } catch (error) {
      console.error('Error updating project type:', error);
      alert('Error updating project type');
    }
  };

  const handleSaveQuestion = async (question) => {
    try {
      if (question.id && !question.id.startsWith('new-')) {
        await axios.put(`${BACKEND_URL}/api/estimator/questions/${question.id}`, question);
      } else {
        const { id, ...newQuestion } = question;
        await axios.post(`${BACKEND_URL}/api/estimator/questions`, newQuestion);
      }
      await fetchData();
      setEditingQuestion(null);
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Error saving question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/estimator/questions/${questionId}`);
      await fetchData();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Error deleting question');
    }
  };

  const addNewQuestion = (projectType) => {
    const existingQuestions = questions.filter(q => q.project_type === projectType);
    setEditingQuestion({
      id: `new-${Date.now()}`,
      project_type: projectType,
      question: '',
      description: '',
      field_type: 'select',
      options: [{ id: 'opt1', label: 'Option 1', value: 1.0, value_type: 'multiplier' }],
      order: existingQuestions.length + 1,
      is_required: true,
      is_active: true
    });
  };

  const duplicateQuestion = (question, newProjectType) => {
    const existingQuestions = questions.filter(q => q.project_type === newProjectType);
    setEditingQuestion({
      ...question,
      id: `new-${Date.now()}`,
      project_type: newProjectType,
      order: existingQuestions.length + 1
    });
  };

  // Get all unique project type keys (including subtypes like new-build-home)
  const getQuestionGroups = () => {
    const groups = [];
    
    projectTypes.forEach(type => {
      if (type.has_subtypes && type.subtypes) {
        type.subtypes.forEach(subtype => {
          const key = `${type.slug}-${subtype.id}`;
          groups.push({
            key,
            name: `${type.name} - ${subtype.name}`,
            parentType: type,
            subtype: subtype,
            questions: questions.filter(q => q.project_type === key)
          });
        });
      } else {
        groups.push({
          key: type.slug,
          name: type.name,
          parentType: type,
          subtype: null,
          questions: questions.filter(q => q.project_type === type.slug)
        });
      }
    });
    
    return groups;
  };

  const questionGroups = getQuestionGroups();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AdminSidebar pageTitle="Estimator Settings" pageIcon={Calculator}>
      {/* Header Actions */}
      <div className="flex justify-end mb-4">
        <Link to="/estimator" target="_blank">
          <Button variant="outline" size="sm">
            Preview Estimator
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b rounded-t-xl mb-0">
        <div className="flex gap-6 px-4">
          <button
            onClick={() => setActiveTab('types')}
            className={`py-3 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'types' 
                ? 'border-cyan-600 text-cyan-600' 
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Project Types & Base Pricing
            </div>
          </button>
          <button
            onClick={() => setActiveTab('questions')}
            className={`py-3 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'questions' 
                ? 'border-cyan-600 text-cyan-600' 
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Questions & Pricing Logic
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="mt-0">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Project Types Tab */}
            {activeTab === 'types' && (
              <div className="space-y-4">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-2">Project Types & Base Pricing</h2>
                  <p className="text-sm text-slate-600 mb-6">
                    Configure the base price per square foot for each project type. This is the starting point for all estimates.
                  </p>
                  
                  <div className="space-y-3">
                    {projectTypes.map((type) => (
                      <div key={type.id} className="border border-slate-200 rounded-lg">
                        {editingType === type.id ? (
                          <div className="p-4 space-y-4">
                            <div className="grid sm:grid-cols-3 gap-4">
                              <div>
                                <Label className="text-xs">Name</Label>
                                <Input
                                  value={type.name}
                                  onChange={(e) => setProjectTypes(types => 
                                    types.map(t => t.id === type.id ? {...t, name: e.target.value} : t)
                                  )}
                                  className="h-9"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Base Price per SF ($)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={type.base_price_per_sqft}
                                  onChange={(e) => setProjectTypes(types => 
                                    types.map(t => t.id === type.id ? {...t, base_price_per_sqft: parseFloat(e.target.value)} : t)
                                  )}
                                  className="h-9"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Min Project Size (SF)</Label>
                                <Input
                                  type="number"
                                  value={type.min_project_size}
                                  onChange={(e) => setProjectTypes(types => 
                                    types.map(t => t.id === type.id ? {...t, min_project_size: parseInt(e.target.value)} : t)
                                  )}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Description</Label>
                              <Input
                                value={type.description || ''}
                                onChange={(e) => setProjectTypes(types => 
                                  types.map(t => t.id === type.id ? {...t, description: e.target.value} : t)
                                )}
                                className="h-9"
                              />
                            </div>
                            
                            {/* Subtypes */}
                            {type.has_subtypes && type.subtypes && (
                              <div className="border-t pt-4">
                                <Label className="text-xs mb-2 block">Subtypes</Label>
                                <div className="space-y-2">
                                  {type.subtypes.map((subtype, idx) => (
                                    <div key={subtype.id} className="flex gap-2 items-center bg-slate-50 p-2 rounded">
                                      <Input
                                        value={subtype.name}
                                        onChange={(e) => {
                                          const newSubtypes = [...type.subtypes];
                                          newSubtypes[idx] = {...subtype, name: e.target.value};
                                          setProjectTypes(types => 
                                            types.map(t => t.id === type.id ? {...t, subtypes: newSubtypes} : t)
                                          );
                                        }}
                                        placeholder="Subtype name"
                                        className="h-8 flex-1"
                                      />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={subtype.base_price_per_sqft}
                                        onChange={(e) => {
                                          const newSubtypes = [...type.subtypes];
                                          newSubtypes[idx] = {...subtype, base_price_per_sqft: parseFloat(e.target.value)};
                                          setProjectTypes(types => 
                                            types.map(t => t.id === type.id ? {...t, subtypes: newSubtypes} : t)
                                          );
                                        }}
                                        placeholder="$/SF"
                                        className="h-8 w-24"
                                      />
                                      <span className="text-xs text-slate-500">$/SF</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="flex gap-2 pt-2">
                              <Button 
                                size="sm"
                                onClick={() => handleUpdateType(type.id, type)}
                                className="bg-cyan-600 hover:bg-cyan-700"
                              >
                                <Save className="w-4 h-4 mr-1" />
                                Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingType(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 flex justify-between items-center">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <h3 className="font-semibold text-slate-900">{type.name}</h3>
                                <span className="text-sm font-medium text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded">
                                  ${type.base_price_per_sqft}/SF
                                </span>
                                {type.has_subtypes && (
                                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                    {type.subtypes?.length || 0} subtypes
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 mt-1">{type.description}</p>
                              {type.has_subtypes && type.subtypes && (
                                <div className="flex gap-2 mt-2">
                                  {type.subtypes.map(st => (
                                    <span key={st.id} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                                      {st.name}: ${st.base_price_per_sqft}/SF
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setEditingType(type.id)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Questions Tab */}
            {activeTab === 'questions' && (
              <div className="space-y-4">
                {questionGroups.map((group) => (
                  <div key={group.key} className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
                      <div>
                        <h2 className="font-semibold text-slate-900">{group.name}</h2>
                        <p className="text-xs text-slate-500">
                          {group.questions.length} questions • 
                          Base: ${group.subtype?.base_price_per_sqft || group.parentType.base_price_per_sqft}/SF
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => addNewQuestion(group.key)}
                        className="bg-cyan-600 hover:bg-cyan-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    
                    <div className="divide-y divide-slate-100">
                      {group.questions
                        .sort((a, b) => a.order - b.order)
                        .map((question) => (
                          <QuestionItem
                            key={question.id}
                            question={question}
                            isEditing={editingQuestion?.id === question.id}
                            editingData={editingQuestion?.id === question.id ? editingQuestion : null}
                            onEdit={() => setEditingQuestion({...question})}
                            onSave={handleSaveQuestion}
                            onCancel={() => setEditingQuestion(null)}
                            onDelete={() => handleDeleteQuestion(question.id)}
                            onChange={(data) => setEditingQuestion({...editingQuestion, ...data})}
                            onDuplicate={(newType) => duplicateQuestion(question, newType)}
                            questionGroups={questionGroups}
                          />
                        ))}
                      
                      {editingQuestion?.id?.startsWith('new-') && editingQuestion.project_type === group.key && (
                        <QuestionItem
                          question={editingQuestion}
                          isEditing={true}
                          editingData={editingQuestion}
                          onSave={handleSaveQuestion}
                          onCancel={() => setEditingQuestion(null)}
                          onChange={(data) => setEditingQuestion({...editingQuestion, ...data})}
                          questionGroups={questionGroups}
                        />
                      )}
                      
                      {group.questions.length === 0 && !editingQuestion?.project_type?.startsWith(group.key) && (
                        <div className="p-8 text-center text-slate-500 text-sm">
                          No questions yet. Click "Add" to create one.
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AdminSidebar>
  );
};

// Question Item Component
const QuestionItem = ({ question, isEditing, editingData, onEdit, onSave, onCancel, onDelete, onChange, onDuplicate, questionGroups }) => {
  const data = editingData || question;

  const addOption = () => {
    const newOptions = [...(data.options || []), {
      id: `opt-${Date.now()}`,
      label: 'New Option',
      value: 1.0,
      value_type: 'multiplier'
    }];
    onChange({ options: newOptions });
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...data.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    onChange({ options: newOptions });
  };

  const removeOption = (index) => {
    const newOptions = data.options.filter((_, i) => i !== index);
    onChange({ options: newOptions });
  };

  const formatValue = (opt) => {
    if (opt.value_type === 'multiplier') {
      const pct = (opt.value - 1) * 100;
      if (pct === 0) return 'No change';
      return pct > 0 ? `+${pct.toFixed(0)}%` : `${pct.toFixed(0)}%`;
    }
    if (opt.value_type === 'fixed') return `+$${opt.value.toLocaleString()}`;
    if (opt.value_type === 'base_sqft_price') return `$${opt.value}/SF base`;
    return opt.value;
  };

  if (isEditing) {
    return (
      <div className="p-4 bg-cyan-50 border-l-4 border-cyan-500">
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-xs">Question</Label>
            <Input
              value={data.question}
              onChange={(e) => onChange({ question: e.target.value })}
              placeholder="Enter question..."
              className="h-9"
            />
          </div>
          <div>
            <Label className="text-xs">Field Type</Label>
            <Select
              value={data.field_type}
              onValueChange={(value) => onChange({ field_type: value })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="select">Select (Multiple Choice)</SelectItem>
                <SelectItem value="slider">Slider</SelectItem>
                <SelectItem value="number">Number Input</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="mb-3">
          <Label className="text-xs">Description (optional)</Label>
          <Input
            value={data.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Help text..."
            className="h-9"
          />
        </div>

        {/* Slider Settings */}
        {data.field_type === 'slider' && (
          <div className="bg-white rounded-lg p-3 mb-3 space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Min Value</Label>
                <Input
                  type="number"
                  value={data.min_value || 0}
                  onChange={(e) => onChange({ min_value: parseInt(e.target.value) })}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Max Value</Label>
                <Input
                  type="number"
                  value={data.max_value || 10000}
                  onChange={(e) => onChange({ max_value: parseInt(e.target.value) })}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Step</Label>
                <Input
                  type="number"
                  value={data.step || 100}
                  onChange={(e) => onChange({ step: parseInt(e.target.value) })}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Default</Label>
                <Input
                  type="number"
                  value={data.default_value || data.min_value || 0}
                  onChange={(e) => onChange({ default_value: parseInt(e.target.value) })}
                  className="h-8"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Unit</Label>
                <Input
                  value={data.unit || 'SF'}
                  onChange={(e) => onChange({ unit: e.target.value })}
                  placeholder="SF"
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Slider Type (optional)</Label>
                <Select
                  value={data.slider_type || 'default'}
                  onValueChange={(value) => onChange({ slider_type: value === 'default' ? null : value })}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default (just number)</SelectItem>
                    <SelectItem value="garage_size">Garage Size (with labels)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {data.slider_type === 'garage_size' && (
              <div>
                <Label className="text-xs">Size Labels (JSON)</Label>
                <Textarea
                  value={JSON.stringify(data.labels || [], null, 2)}
                  onChange={(e) => {
                    try {
                      onChange({ labels: JSON.parse(e.target.value) });
                    } catch {}
                  }}
                  placeholder='[{"min": 300, "max": 799, "label": "1-Car"}]'
                  rows={3}
                  className="text-xs font-mono"
                />
              </div>
            )}
          </div>
        )}

        {/* Number Input Settings */}
        {data.field_type === 'number' && (
          <div className="grid sm:grid-cols-4 gap-3 mb-3">
            <div>
              <Label className="text-xs">Unit</Label>
              <Input
                value={data.unit || ''}
                onChange={(e) => onChange({ unit: e.target.value })}
                placeholder="sqft"
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Min Value</Label>
              <Input
                type="number"
                value={data.min_value || ''}
                onChange={(e) => onChange({ min_value: parseFloat(e.target.value) })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Max Value</Label>
              <Input
                type="number"
                value={data.max_value || ''}
                onChange={(e) => onChange({ max_value: parseFloat(e.target.value) })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Price/Unit ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={data.price_per_unit || 0}
                onChange={(e) => onChange({ price_per_unit: parseFloat(e.target.value) })}
                className="h-8"
              />
            </div>
          </div>
        )}

        {/* Select Options */}
        {data.field_type === 'select' && (
          <div className="mb-3">
            <div className="flex justify-between items-center mb-2">
              <Label className="text-xs">Options & Pricing</Label>
              <Button size="sm" variant="outline" onClick={addOption} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" />
                Add Option
              </Button>
            </div>
            <div className="space-y-2">
              {(data.options || []).map((option, index) => (
                <div key={option.id || index} className="flex gap-2 items-center bg-white p-2 rounded border">
                  <Input
                    value={option.id}
                    onChange={(e) => updateOption(index, 'id', e.target.value)}
                    placeholder="id"
                    className="w-24 h-8 text-xs"
                  />
                  <Input
                    value={option.label}
                    onChange={(e) => updateOption(index, 'label', e.target.value)}
                    placeholder="Label"
                    className="flex-1 h-8"
                  />
                  <Select
                    value={option.value_type}
                    onValueChange={(value) => updateOption(index, 'value_type', value)}
                  >
                    <SelectTrigger className="w-28 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="multiplier">Multiplier</SelectItem>
                      <SelectItem value="fixed">Fixed $</SelectItem>
                      <SelectItem value="base_sqft_price">$/SF Base</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    value={option.value}
                    onChange={(e) => updateOption(index, 'value', parseFloat(e.target.value))}
                    className="w-20 h-8"
                  />
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => removeOption(index)}
                    className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Multiplier: 1.0 = no change, 1.25 = +25%, 0.9 = -10% | Fixed: adds flat $ | $/SF Base: overrides base price
            </p>
          </div>
        )}

        {/* Conditional Logic */}
        <div className="mb-3">
          <Label className="text-xs">Depends On (optional)</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <Input
              type="number"
              value={data.depends_on?.question_order || ''}
              onChange={(e) => onChange({ 
                depends_on: e.target.value ? {
                  ...data.depends_on,
                  question_order: parseInt(e.target.value)
                } : null 
              })}
              placeholder="Question #"
              className="h-8"
            />
            <Input
              value={data.depends_on?.value || ''}
              onChange={(e) => onChange({ 
                depends_on: {
                  ...data.depends_on,
                  value: e.target.value || null,
                  not_value: null
                } 
              })}
              placeholder="Show if value ="
              className="h-8"
            />
            <Input
              value={data.depends_on?.not_value || ''}
              onChange={(e) => onChange({ 
                depends_on: {
                  ...data.depends_on,
                  not_value: e.target.value || null,
                  value: null
                } 
              })}
              placeholder="Show if NOT ="
              className="h-8"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Order:</Label>
              <Input
                type="number"
                value={data.order}
                onChange={(e) => onChange({ order: parseInt(e.target.value) })}
                className="w-16 h-8"
              />
            </div>
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={data.is_required}
                onChange={(e) => onChange({ is_required: e.target.checked })}
                className="rounded"
              />
              Required
            </label>
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={data.is_active}
                onChange={(e) => onChange({ is_active: e.target.checked })}
                className="rounded"
              />
              Active
            </label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSave(data)} className="bg-cyan-600 hover:bg-cyan-700 h-8">
              <Save className="w-4 h-4 mr-1" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel} className="h-8">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-3 flex justify-between items-start ${!question.is_active ? 'opacity-50 bg-slate-50' : ''}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
            #{question.order}
          </span>
          <span className="font-medium text-slate-900 text-sm">{question.question}</span>
          <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
            {question.field_type}
          </span>
          {!question.is_active && (
            <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Inactive</span>
          )}
          {question.depends_on && (
            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
              Conditional
            </span>
          )}
        </div>
        
        {question.field_type === 'select' && question.options && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {question.options.slice(0, 5).map((opt) => (
              <span key={opt.id} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                {opt.label}: {formatValue(opt)}
              </span>
            ))}
            {question.options.length > 5 && (
              <span className="text-xs text-slate-400">+{question.options.length - 5} more</span>
            )}
          </div>
        )}
        
        {question.field_type === 'slider' && (
          <p className="text-xs text-slate-500 mt-1">
            Slider: {question.min_value?.toLocaleString()} - {question.max_value?.toLocaleString()} {question.unit}
            {question.slider_type && ` (${question.slider_type})`}
          </p>
        )}
      </div>
      <div className="flex gap-1 ml-2">
        <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 w-7 p-0">
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={onDelete}
          className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default AdminEstimatorPage;
